import { useState } from "react";
import { Dayjs } from "dayjs";
import {
  QueryOperator,
  EnumOperator,
  DateTimeOperator,
  CustomMetadataLinkOperators,
  CUSTOM_METADATA_ENUM_OPERATORS,
  CUSTOM_METADATA_DATETIME_OPERATORS,
  CUSTOM_METADATA_LINK_OPERATORS,
  FilterOption,
  CustomMetadata,
  CustomMetadataFieldType,
  APIQueryOperators,
  VqlModeOps,
} from "types";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { useCustomMetadataModal } from "hooks/useCustomMetadataModal";
import {
  parseDateFromAPI,
  parseDateRangeFromAPI,
} from "helpers/utility/customMetadataHelpers";

type FieldType = Exclude<
  CustomMetadataFieldType,
  "string" | "float" | "geolocation"
>;

interface UseModalOperatorsAndValuesProps {
  type: FieldType;
  field: CustomMetadata;
  editQueryId?: string;
  options: string[];
}

// Base interface with truly common properties
interface BaseReturn<T = any> {
  operator: T;
  operatorOptions: T[];
  setOperator: (op: T) => void;
  previousValue?: string[];
  previousOperator?: EnumOperator | null;
}

// Specific return types extending the base
interface EnumReturn extends BaseReturn<EnumOperator> {
  selectedIds: number[];
  values: FilterOption[];
  setValues: (v: FilterOption[]) => void;
  searchText: string;
  setSearchText: (s: string) => void;
}

interface LinkReturn extends BaseReturn<CustomMetadataLinkOperators> {
  searchText: string;
  selectedIds: number[];
  values: FilterOption[];
  mode: "text" | "multiple";
  setSearchText: (s: string) => void;
  setValues: (v: FilterOption[]) => void;
}

interface DatetimeReturn extends BaseReturn<DateTimeOperator | null> {
  value: { single: Dayjs | null; range: [Dayjs, Dayjs] | null };
  setValue: (v: { single: Dayjs | null; range: [Dayjs, Dayjs] | null }) => void;
  mode: "Single" | "Range";
}

// Centralized configuration
const FIELD_CONFIG = {
  enum: {
    operators: CUSTOM_METADATA_ENUM_OPERATORS,
    defaultOperator: QueryOperator.IS_ONE_OF,
    isMultiValue: true,
    needsValues: true,
    needsSearchText: false,
    needsDateTimeValue: false,
  },
  link: {
    operators: CUSTOM_METADATA_LINK_OPERATORS,
    defaultOperator: QueryOperator.CONTAINS,
    isMultiValue: false,
    needsValues: true,
    needsSearchText: true,
    needsDateTimeValue: false,
  },
  datetime: {
    operators: CUSTOM_METADATA_DATETIME_OPERATORS,
    defaultOperator: null,
    isMultiValue: false,
    needsValues: false,
    needsSearchText: false,
    needsDateTimeValue: true,
  },
} as const;

const formatOptions = (
  options: string[],
  selected?: string[]
): FilterOption[] =>
  options.map((text, id) => ({
    id,
    text,
    selected: selected?.includes(text) || false,
  }));

// Helper to determine datetime mode based on operator
const getDateTimeMode = (
  operator: DateTimeOperator | null
): "Single" | "Range" => {
  if (operator === QueryOperator.BETWEEN) return "Range";
  return "Single";
};

// Function overloads
export function useModalOperatorsAndValues(
  props: UseModalOperatorsAndValuesProps & { type: "enum" }
): EnumReturn;
export function useModalOperatorsAndValues(
  props: UseModalOperatorsAndValuesProps & { type: "link" }
): LinkReturn;
export function useModalOperatorsAndValues(
  props: UseModalOperatorsAndValuesProps & { type: "datetime" }
): DatetimeReturn;
export function useModalOperatorsAndValues({
  type,
  field,
  editQueryId,
  options,
}: UseModalOperatorsAndValuesProps) {
  const { oldQuery } = useCustomMetadataModal({ field, editQueryId });
  const oldValue = oldQuery?.custom_metadata?.value;
  const oldOp = oldQuery?.custom_metadata?.op;

  const config = FIELD_CONFIG[type];
  const defaultOp = oldOp ? convertAPIOperator(oldOp) : config.defaultOperator;

  // Common state
  const [operator, setOperator] = useState(defaultOp);

  // Conditional states based on config
  const isMulti =
    config.isMultiValue ||
    (type === "link" && operator === QueryOperator.IS_ONE_OF);

  const [values, setValues] = useState<FilterOption[]>(() =>
    config.needsValues
      ? isMulti
        ? formatOptions(options, oldValue as string[])
        : formatOptions(options)
      : []
  );

  const [searchText, setSearchText] = useState(() =>
    config.needsSearchText && !isMulti ? (oldValue as string) || "" : ""
  );

  const [dateTimeValue, setDateTimeValue] = useState(() => ({
    single:
      config.needsDateTimeValue && !Array.isArray(oldValue)
        ? parseDateFromAPI(oldValue as string)
        : null,
    range:
      config.needsDateTimeValue && Array.isArray(oldValue)
        ? parseDateRangeFromAPI(oldValue as [string, string])
        : null,
  }));

  // Common computed values
  const selectedIds = values.filter((v) => v.selected).map((v) => v.id);
  const operatorOptions = [...config.operators];

  // Common return properties
  const commonReturn = { operatorOptions, setOperator };

  // Type-specific return builders with shared properties
  const returnBuilders: Record<FieldType, () => any> = {
    enum: () => ({
      ...commonReturn,
      operator: operator as EnumOperator,
      values,
      setValues,
      selectedIds,
      searchText,
      setSearchText,
      previousValue: oldValue,
      previousOperator: defaultOp,
    }),

    link: () => ({
      ...commonReturn,
      operator: operator as CustomMetadataLinkOperators,
      values,
      setValues,
      selectedIds,
      searchText,
      setSearchText,
      mode: operator === QueryOperator.IS_ONE_OF ? "multiple" : "text",
      previousValue: oldValue,
      previousOperator: convertAPIOperator(
        oldOp as APIQueryOperators | VqlModeOps
      ),
    }),

    datetime: () => ({
      ...commonReturn,
      operator: operator as DateTimeOperator | null,
      value: dateTimeValue,
      setValue: setDateTimeValue,
      mode: getDateTimeMode(operator as DateTimeOperator | null),
    }),
  };

  return returnBuilders[type]();
}
